import React, { useEffect, useMemo, useState } from 'react';

import FluxoAdminEtapaForm from './FluxoAdminEtapaForm';
import BoxConteudo from '../../../../../../../components/BoxConteudo';
import CustomButton from '../../../../../../../components/CustomButton';
import CustomCol from '../../../../../../../components/CustomCol';
import { type CustomTableHeader } from '../../../../../../../components/CustomTable';
import DetailsTable from '../../../../../../../components/CustomTable/DetailsTable';
import CustomTableOffcanvas from '../../../../../../../components/CustomTableOffcanvas';
import { getUser } from '../../../../../../../modules/contexts/Auth/RequireAuth';
import { IFluxoAdminEtapaRegistro } from '../../../../../../../modules/types/FluxoAdmin';
import { getFluxoAdminEtapasByModeloId } from '../../../../../../../services/fluxosAdmin/fluxoAdminEtapaService';
import theme from '../../../../../../../theme';
import { useExcelExport } from '../../../../../../../utils/excel-export';
import {
  formataNomeMeioSiglas,
  formatDate,
  formatDateToFile,
} from '../../../../../../../utils/functions';

type EtapaTableRow = {
  id: number;
  ordem: number;
  etapa: string;
  areaResponsavel: string;
  estimativaDias: number;
};

const headersTable: CustomTableHeader<EtapaTableRow>[] = [
  { text: 'ID', key: 'id', width: 6 },
  { text: 'Ordem', key: 'ordem', width: 10 },
  { text: 'Etapa', key: 'etapa', width: 38 },
  { text: 'Área Responsável', key: 'areaResponsavel', width: 24 },
  { text: 'Estimativa (dias)', key: 'estimativaDias', width: 18 },
];

const valoresOffcanvas: Record<string, string> = {
  id: 'ID',
  dataRegistro: 'Registro',
  dataUltimaAtualizacao: 'Última Atualização',
  dataUltimaVisualizacao: 'Última Visualização',
  nEdicoes: 'Edições',
  nVisualizacoes: 'Visualizações',
  nVisualizadores: 'Visualizadores',

  separadorLog: '',
  separadorDados: '',

  ordem: 'Ordem',
  etapa: 'Etapa',
  areaResponsavel: 'Área Responsável',
  estimativaDias: 'Estimativa (dias)',
  descricao: 'Descrição',
  observacao: 'Observação',
};

const badge = (text: string) => (
  <span
    style={{
      backgroundColor: theme.colors.verdegov,
      color: theme.colors.white,
      borderRadius: '8px',
      padding: '6px 10px',
      display: 'inline-block',
      minWidth: '70px',
      textAlign: 'center',
      fontWeight: 600,
    }}
  >
    {text}
  </span>
);

interface FluxoAdminEtapasProps {
  modeloId: number;
  onLogsRefresh?: () => void;
}

const FluxoAdminEtapas: React.FC<FluxoAdminEtapasProps> = ({
  modeloId,
  onLogsRefresh,
}) => {
  const user = getUser();
  const userID = user.sub;

  const [etapas, setEtapas] = useState<IFluxoAdminEtapaRegistro[]>([]);
  const [selectedOffcanvasId, setSelectedOffcanvasId] = useState<number | null>(
    null,
  );
  const [selectedEtapa, setSelectedEtapa] =
    useState<IFluxoAdminEtapaRegistro | null>(null);
  const [showForm, setShowForm] = useState(false);

  const fetchEtapas = async () => {
    try {
      const registros = await getFluxoAdminEtapasByModeloId(modeloId);
      setEtapas(registros);

      if (selectedEtapa) {
        const atualizado =
          registros.find((item) => item.id === selectedEtapa.id) || null;
        setSelectedEtapa(atualizado);
      } else if (registros.length > 0) {
        const ultimo =
          registros.reduce(
            (prev, curr) => (prev.id > curr.id ? prev : curr),
            registros[0],
          ) || registros[0];
        setSelectedEtapa(ultimo);
        setSelectedOffcanvasId(ultimo.id);
      } else {
        setSelectedEtapa(null);
      }
    } catch (error) {
      console.error('Erro ao buscar etapas:', error);
      setEtapas([]);
    }
  };

  useEffect(() => {
    if (modeloId) {
      fetchEtapas();
    }
  }, [modeloId]);

  useEffect(() => {
    setSelectedEtapa(null);
    setSelectedOffcanvasId(null);
  }, [modeloId]);

  const ordensExistentes = useMemo(
    () => etapas.map((item) => item.ordem),
    [etapas],
  );

  const tableData: EtapaTableRow[] = useMemo(
    () =>
      etapas.map((item) => ({
        id: item.id,
        ordem: item.ordem,
        etapa: item.etapa,
        areaResponsavel: item.areaResponsavel,
        estimativaDias: item.estimativaDias,
      })),
    [etapas],
  );

  const offCanvasData = useMemo(
    () =>
      etapas.map((item) => ({
        id: item.id,
        dataRegistro:
          formatDate(item.dataRegistro) +
          ' - ' +
          formataNomeMeioSiglas(item.usuarioRegistro.nomeCompleto),
        dataUltimaAtualizacao:
          formatDate(item.dataUltimaAtualizacao) +
          ' - ' +
          formataNomeMeioSiglas(item.usuarioAtualizacao.nomeCompleto),
        dataUltimaVisualizacao:
          formatDate(item.dataUltimaVisualizacao) +
          ' - ' +
          formataNomeMeioSiglas(item.usuarioUltimaVisualizacao.nomeCompleto),
        nEdicoes: item.nEdicoes,
        nVisualizacoes: item.nVisualizacoes,
        nVisualizadores: item.nVisualizadores,

        separadorLog: '',

        separadorDados: '',
        ordem: badge(String(item.ordem)),
        etapa: item.etapa,
        areaResponsavel: item.areaResponsavel,
        estimativaDias: item.estimativaDias,
        descricao: item.descricao || 'Não informado',
        observacao: item.observacao || 'Não informado',
      })),
    [etapas],
  );

  const handleSelectEtapa = (row: EtapaTableRow) => {
    const registro =
      etapas.find((etapa) => String(etapa.id) === String(row.id)) || null;
    setSelectedEtapa(registro);
    setShowForm(true);
  };

  const handleOpenDetails = (row: EtapaTableRow) => {
    if (!row?.id) return;
    setSelectedOffcanvasId(row.id);
  };

  const handleNew = () => {
    setSelectedEtapa(null);
    setShowForm(true);
  };

  const { exportData, isExporting } = useExcelExport(
    'listagemfluxosadminetapas',
  );

  const handleExport = async () => {
    try {
      await exportData(
        etapas,
        `sisaf_fluxosadmin_etapas_${formatDateToFile(
          new Date().toISOString(),
        )}.xlsx`,
        userID,
        'Listagem de Etapas de Fluxos Administrativos',
        'DAF',
      );
    } catch (error) {
      console.error('Erro na exportação:', error);
    }
  };

  return (
    <BoxConteudo boxTitulo="Etapas do Modelo">
      <CustomTableOffcanvas
        headers={headersTable}
        data={tableData}
        displayValues={valoresOffcanvas}
        offcanvasTitle="Etapa do Fluxo Administrativo"
        widthOffcanvas="60%"
        onOpenOffcanvas={handleOpenDetails}
        onRowClick={handleSelectEtapa}
        itemsPerPage={20}
        showPagination={false}
        showOpenButton
        filterable={false}
        sortable
        minHeight="400px"
      >
        {(row) => (
          <DetailsTable
            rows={offCanvasData}
            selectedId={row?.id ?? selectedOffcanvasId}
            displayValues={valoresOffcanvas}
          />
        )}
      </CustomTableOffcanvas>

      <CustomCol className="d-flex justify-content-end gap-3 mt-3">
        <CustomButton
          label="Exportar Dados"
          onClick={handleExport}
          deactivate={isExporting}
          bgColor={theme.colors.botaoAcao.amarelo}
          textColor={theme.colors.black}
        />
        <CustomButton
          label="Nova Etapa"
          onClick={handleNew}
          bgColor={theme.colors.botaoAcao.azul}
          textColor={theme.colors.white}
        />
      </CustomCol>

      {showForm && (
        <FluxoAdminEtapaForm
          modeloId={modeloId}
          registro={selectedEtapa || undefined}
          onClose={() => setShowForm(false)}
          onSaved={() => {
            fetchEtapas();
          }}
          onLogsRefresh={onLogsRefresh}
          existingOrders={
            selectedEtapa
              ? ordensExistentes.filter(
                  (ordem) => ordem !== selectedEtapa.ordem,
                )
              : ordensExistentes
          }
        />
      )}
    </BoxConteudo>
  );
};

export default FluxoAdminEtapas;
